import {
    AzureLayer,
    getLayerFilter, getMinAndMaxZoomOpts,
    isHeatMapDisabledXorIsOverlayPinEnabled, isLabelBoxEnabled
} from "@esWebAPI/es/map/layer/azure/AzureLayer";
import {layer, Map, Shape, source, data, SymbolLayerOptions} from "azure-maps-control";
import {ILayerView, LayerInfo} from "@esWebAPI/es/model/Layer";
import {DatasourceManager} from "@esWebAPI/es/map/module/DatasourceManager";
import {LabelThematicStyler} from "@esWebAPI/es/map/styler/LabelThematicStyler";
import {AzureImageSpriteManager} from "@esWebAPI/es/map/layer/AzureImageSpriteManager";
import {ColorThematic, SizeThematic} from "@esWebAPI/es/model/Thematic";
import {ColorThematicStyler} from "@esWebAPI/es/map/styler/ColorThematicStyler";
import {SizeThematicStyler} from "@esWebAPI/es/map/styler/SizeThematicStyler";
import {ThematicStylerFactory} from "@esWebAPI/es/map/styler/ThematicStylerFactory";
import {DataDrivenPropertyValueSpecification, ExpressionFilterSpecification} from "@maplibre/maplibre-gl-style-spec";
import {Feature} from "geojson";
import {GeomUtils} from "@esWebAPI/es/util/GeomUtils";

export class AzureLabelLayer implements AzureLayer {

    private readonly _map: Map;
    private readonly datasourceManager: DatasourceManager;
    private readonly spriteManager: AzureImageSpriteManager;
    private readonly colorStyler: ColorThematicStyler;
    private readonly sizeStyler: SizeThematicStyler;

    private _layerView: ILayerView;
    private _layerInfo: LayerInfo;
    private _layer: layer.SymbolLayer|undefined

    private colorThematic: ColorThematic|undefined;
    private sizeThematic: SizeThematic|undefined;
    private layerFilter: ExpressionFilterSpecification|undefined;

    private labelStyler: LabelThematicStyler|undefined;

    private datasourceId: string|undefined;

    private polygonLabelDatasource : undefined | source.DataSource;

    constructor(map: Map, layerView: ILayerView, layerInfo: LayerInfo,
                datasourceManager: DatasourceManager,
                spriteManager: AzureImageSpriteManager,
                stylerFactory: ThematicStylerFactory,
                layerFilter?: ExpressionFilterSpecification) {

        this._map = map;
        this._layerView = layerView;
        this._layerInfo = layerInfo;
        this.datasourceManager = datasourceManager;
        this.spriteManager = spriteManager;
        this.colorStyler = stylerFactory.getColorStyler(layerView);
        this.sizeStyler = stylerFactory.getSizeStyler();
        this.layerFilter = layerFilter;
    }

    private isLabellingEnabled(){
        return this._layerView.styleSettings?.labelSettings?.enabled;
    }

    private isLeaderLineLabellingEnabled() {
        // todo: check if enabled == true too?
        // this.layerView.styleSettings?.labelSettings?.enabled &&
        return this._layerView.styleSettings?.labelSettings?.labelling === "LEADER_LINES";
    }

    /**
     *  Azure does not expose the icon-text-fit property so we need to set it in the MapLibre map once the layer is loaded
     */
    private applyMapLibreBoxIconsOptions() {
        const labelLayerId = this.getLayerId();
        // @ts-expect-error
        const mapLibreMap = this._map.map;

        if (!mapLibreMap.getLayer(labelLayerId)) {
            // TOR9-21433 Layer was destroyed / removed
            return;
        }

        if (!isLabelBoxEnabled(this._layerView)) {
            mapLibreMap.setLayoutProperty(labelLayerId, "icon-text-fit", "none");
            mapLibreMap.setLayoutProperty(labelLayerId, "icon-padding", 0);
            mapLibreMap.setLayoutProperty(labelLayerId, "icon-size", 0);
            return;
        }

        mapLibreMap.setLayoutProperty(labelLayerId, "icon-text-fit", "both");
        mapLibreMap.setLayoutProperty(labelLayerId, "icon-padding", 5);
        mapLibreMap.setLayoutProperty(labelLayerId, "icon-size", 1);
        mapLibreMap.setPaintProperty(labelLayerId, "icon-color", this.labelStyler?.getBoxFillColor(this.colorStyler) ?? "white");
        mapLibreMap.setPaintProperty(labelLayerId, "icon-halo-color", this.labelStyler?.getBoxOutlineColor() ?? "black");
        mapLibreMap.setPaintProperty(labelLayerId, "icon-halo-width", 3);
        mapLibreMap.setPaintProperty(labelLayerId, "icon-halo-blur", 0);
    }

    private async updateThematics(): Promise<void> {
        this.colorThematic = await this.colorStyler.getThematic(this._layerView);
        this.sizeThematic = await this.sizeStyler.getThematic(this._layerView);
    }

    public getOptions(): SymbolLayerOptions {

        this.labelStyler = new LabelThematicStyler(
            this._layerView,
            this._layerInfo.attributes,
            this.spriteManager,
            this.sizeThematic,
            this.colorThematic
        );

        return {
            sourceLayer: this.datasourceId && this.datasourceManager.isPbf(this.datasourceId) ? 'es' : undefined,
            iconOptions: this.labelStyler.getIconOptions(),
            textOptions: this.labelStyler.getLabelOptions(undefined, this.colorStyler),
            visible: this._layerView.enabled && this.isLabellingEnabled() &&
                isHeatMapDisabledXorIsOverlayPinEnabled(this._layerView) && !this.isLeaderLineLabellingEnabled(),
            filter: getLayerFilter(this._layerView, this.layerFilter, true),
            ...getMinAndMaxZoomOpts(this._layerView)
        }
    }

    public create(
        datasource: source.Source,
        colorThematic?: ColorThematic,
        sizeThematic?: SizeThematic
    ): Promise<void> {

        this.datasourceId = datasource.getId();
        this.colorThematic = colorThematic;
        this.sizeThematic = sizeThematic;

        return new Promise((resolve) => {
            const addLayer = () => {
                this._map.events.add("layeradded", (layer) => {
                    // If layer is removed, we need to add the map libre options once it's added again
                    if (layer.getId() === this.getLayerId()) {
                        this.applyMapLibreBoxIconsOptions();
                    }
                });

                if (this._layerView.styleSettings.$type === "PolygonStyleSettings") {
                    if (this.datasourceManager.isGeoJson(datasource.getId())) {
                        const polygonDsId = `${this.datasourceId}-polygon-labels`
                        const dsFilter = (datasource as source.DataSource).getOptions().filter;
                        this.polygonLabelDatasource = this.datasourceManager.createBlank(this._layerView, this._layerInfo, polygonDsId, dsFilter);

                        this._layer = new layer.SymbolLayer(this.polygonLabelDatasource, this.getLayerId(), this.getOptions());
                        this._map.layers.add(this._layer);

                        if (this.isLeaderLineLabellingEnabled()){
                            // todo: we'll need to handle events #dataremoved, #dataadded, #shapechanged
                            es.log.warn("Polygon : isLeaderLineLabellingEnabled : skip normal label layer creation");
                            resolve();
                            return;
                        }

                        this.populatePolygonLabels(datasource as source.DataSource);

                        resolve();
                        return;
                    }
                }

                this._layer = new layer.SymbolLayer(datasource.getId(), this.getLayerId(), this.getOptions());
                this._map.layers.add(this._layer);
                resolve();
            };

            // @ts-expect-error
            const mapLibreMap = this._map.map;

            // check if glyphs are available in the style
            if (mapLibreMap && mapLibreMap.getStyle()?.glyphs) {
                es.log.log("AzureLabelLayer : glyph check : glyphs already loaded", this._layerView.id, this._layerView.title);
                addLayer();
            } else if (mapLibreMap) {
                // wait for styledata event which fires when style loads/changes
                es.log.log("AzureLabelLayer : glyph check : waiting for styledata...", this._layerView.id, this._layerView.title);
                const startTime = Date.now();
                mapLibreMap.once('styledata', () => {
                    es.log.log("AzureLabelLayer : glyph check : ...loaded styledata", this._layerView.id, this._layerView.title, "timeTaken", es.util.formatMillis(Date.now() - startTime));
                    addLayer();
                });
            } else {
                // fallback if map object isn't ready (hopefully doesn't happen). just wait before trying and hope for the best
                es.log.warn("AzureLabelLayer : glyph check : fallback", this._layerView.id, this._layerView.title);
                window.setTimeout(() => {
                    addLayer();
                }, 200);
            }
        });
    }

    private populatePolygonLabels(datasource?: source.DataSource) {
        if (!this.polygonLabelDatasource) {
            //console.warn("populatePolygonLabels : no polygonLabelDatasource");
            return;
        }
        if (this.polygonLabelDatasource.getShapes().length > 0) {
            // already have shapes
            return;
        }
        if (!datasource) {
            datasource = this._map.sources.getById(this.datasourceId!) as source.DataSource;
        }
        if (!datasource) {
            //console.log("populatePolygonLabels : no datasource");
            return;
        }
        const shapes = datasource.getShapes();
        const labels = this.calculatePolygonLabels(shapes);
        for (const polygonShape of shapes) {
            this._map.events.add("shapechanged", polygonShape, (shape: Shape) => this.onShapeChanged(shape));
        }
        this.polygonLabelDatasource.add(labels);
    }

    private onShapeChanged(shape: Shape) {
        const vc = GeomUtils.getPolygonVisualCenter(shape.toJson() as Feature);
        if(!vc) {
            return;
        }

        const labelShape = this.polygonLabelDatasource?.getShapeById(shape.getId());
        labelShape?.setCoordinates([vc[0], vc[1]]);
        labelShape?.setProperties(shape.getProperties());
    }

    // Max time (ms) to spend calculating polygon visual centers before abandoning.
    private static readonly POLYGON_LABEL_TIMEOUT_MS = 10000;

    private calculatePolygonLabels(shapes: Shape[]) {
        const startTime = Date.now();
        const labels: Shape[] = [];
        for (const shape of shapes) {
            const elapsed = Date.now() - startTime;
            if (elapsed > AzureLabelLayer.POLYGON_LABEL_TIMEOUT_MS) {
                es.log.error(
                    `calculatePolygonLabels: abandoned after ${es.util.formatMillis(elapsed)} ` +
                    `(${labels.length}/${shapes.length} labels computed). Layer: "${this._layerView.title}" `,
                    `stopped at feat`, shape,
                    `(id=${this._layerView.id}). Consider reducing polygon count or disabling labels.`
                );
                return labels;
            }

            if(shape.data?.properties?.vc) {
                const labelShape = new Shape(new data.Point(shape.data.properties.vc), shape.data.id, shape.data.properties);
                labels.push(labelShape);
                continue;
            }

            const center = GeomUtils.getPolygonVisualCenter(shape.data as Feature);
            if (center) {
                const labelShape = new Shape(new data.Point([center[0], center[1]]), shape.data.id, shape.data.properties);
                labels.push(labelShape);
            }
        }
        es.log.log("calculatePolygonLabels", {
            lvTitle: this._layerView.title,
            timeTaken: es.util.formatMillis(Date.now() - startTime),
            shapeCount: shapes.length,
            lvId: this._layerView.id,
            enabled: this._layerView.enabled,
            labelSettings: this._layerView.styleSettings.labelSettings,
            precision: GeomUtils.POLYLABEL_PRECISION
        });
        return labels;
    }

    public async recreate(layerView: ILayerView, layerInfo: LayerInfo,
                   datasource: source.Source,
                   colorThematic?: ColorThematic,
                   sizeThematic?: SizeThematic) {
        this._layerView = layerView;
        this._layerInfo = layerInfo;
        return this.create(datasource, colorThematic, sizeThematic);
    }

    public async update(layerView: ILayerView, layerInfo: LayerInfo): Promise<void> {
        this._layerView = layerView;
        this._layerInfo = layerInfo;
        await this.updateThematics();
        let options = this.getOptions();
        this._layer?.setOptions(options);

        this.applyMapLibreBoxIconsOptions();
        this.populatePolygonLabels();
    }

    public setLayerFilter(filter?: ExpressionFilterSpecification) {
        this.layerFilter = filter;
    }

    public hide(): void {
        this._layer?.setOptions({visible: false});
    }

    public show(): void {
        this._layer?.setOptions({visible: true});
    }

    public remove(): void {
        const layerId = this.getLayerId();
        if (this._map.layers.getLayerById(layerId)) {
            this._map.layers.remove(layerId);
        }
        this._layer = undefined;
        if (this.polygonLabelDatasource) {
            this.datasourceManager.remove(this.polygonLabelDatasource.getId());
        }
    }

    public hideFeature(featureId: number): void {
        let textOpacity = this.labelStyler?.getLabelOptions().opacity ?? 1;
        const opacity = ['case', ['==', ['id'], featureId], 0, textOpacity] as DataDrivenPropertyValueSpecification<number>;
        this._layer?.setOptions({textOptions: {opacity: opacity}});
        this._layer?.setOptions({iconOptions: {opacity: opacity}});
    }

    public showFeatures(): void {
        let textOpacity = this.labelStyler?.getLabelOptions().opacity ?? 1;
        this._layer?.setOptions({textOptions: {opacity: textOpacity}});
    }

    public setOptions(options: SymbolLayerOptions) {
        this._layer?.setOptions(options);
    }

    public getLayerId(): string {
        return "label_" + this.datasourceId;
    }
}
